// PTE Core interaction inputs, shared by the task trainer and the full mock
// sitting. Each renders one verified Pearson screen behavior: dropdown gaps,
// drag-word pools, re-order boxes with drag AND arrows, yellow single-select,
// typed listening gaps, click-to-highlight incorrect words.
import React, { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp } from 'lucide-react';

import { cn } from '@/lib/utils';

// Defensive coercion for the shape-variant question fields (options,
// paragraphs, word_pool, display_words). Backend contract says these are
// arrays, but a single mistyped item shipping as a string or null used to
// crash the whole practice page at render time (Array.prototype.map on
// non-array). Keep this in one place so every widget stays guarded.
const asArray = (v) => (Array.isArray(v) ? v : []);

export function GapText({ text, renderGap }) {
  const parts = useMemo(() => (text || '').split(/(\{\{\d+\}\})/g), [text]);
  return (
    <p className="text-[16px] leading-9 text-slate-800">
      {parts.map((part, i) => {
        const m = part.match(/^\{\{(\d+)\}\}$/);
        return m ? <React.Fragment key={i}>{renderGap(Number(m[1]))}</React.Fragment> : <span key={i}>{part}</span>;
      })}
    </p>
  );
}

export function FibDropdown({ set, responses, setResp }) {
  const byGap = useMemo(() => Object.fromEntries(set.questions.map((q) => [q.gap, q])), [set]);
  return (
    <GapText
      text={set.stimulus?.text}
      renderGap={(gap) => {
        const q = byGap[gap];
        if (!q) return null;
        const val = responses[q.qid]?.answer_index;
        return (
          <select
            className={cn('mx-1 border bg-white px-2 py-1 text-[15px]', val == null ? 'border-slate-400 text-slate-400' : 'border-blue-900 font-medium text-slate-900')}
            value={val ?? ''}
            data-testid={`fib-dd-${q.qid}`}
            onChange={(e) => setResp(q.qid, { answer_index: e.target.value === '' ? null : Number(e.target.value) })}
          >
            <option value="">Select</option>
            {asArray(q.options).map((opt, oi) => <option key={oi} value={oi}>{opt}</option>)}
          </select>
        );
      }}
    />
  );
}

export function FibDrag({ set, responses, setResp }) {
  const byGap = useMemo(() => Object.fromEntries(set.questions.map((q) => [q.gap, q])), [set]);
  const used = Object.values(responses).map((r) => r?.answer).filter(Boolean);
  const [held, setHeld] = useState(null);

  const place = (q, word) => { setResp(q.qid, { answer: word }); setHeld(null); };
  return (
    <div>
      <GapText
        text={set.stimulus?.text}
        renderGap={(gap) => {
          const q = byGap[gap];
          if (!q) return null;
          const word = responses[q.qid]?.answer;
          return (
            <button
              type="button"
              data-testid={`fib-gap-${q.qid}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); const w = e.dataTransfer.getData('text/plain'); if (w) place(q, w); }}
              onClick={() => {
                if (held) place(q, held);
                else if (word) setResp(q.qid, { answer: null }); // click a filled gap to clear it
              }}
              className={cn(
                'mx-1 inline-block min-w-[90px] border-b-2 px-2 py-0.5 text-center align-baseline text-[15px]',
                word ? 'border-blue-900 bg-blue-50 font-medium text-blue-950' : 'border-slate-400 bg-slate-50 text-slate-300'
              )}
            >
              {word || ' '}
            </button>
          );
        }}
      />
      <div className="mt-6 flex flex-wrap gap-2 border border-blue-200 bg-blue-50/60 p-4" data-testid="fib-pool">
        {asArray(set.word_pool).map((w) => {
          const isUsed = used.includes(w);
          return (
            <button
              key={w}
              type="button"
              draggable={!isUsed}
              onDragStart={(e) => e.dataTransfer.setData('text/plain', w)}
              onClick={() => !isUsed && setHeld(held === w ? null : w)}
              disabled={isUsed}
              className={cn(
                'border px-3 py-1.5 text-sm font-medium',
                isUsed ? 'border-slate-200 bg-slate-100 text-slate-300'
                  : held === w ? 'border-blue-900 bg-blue-900 text-white'
                    : 'border-slate-300 bg-white text-slate-800 hover:border-blue-900'
              )}
            >
              {w}
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-xs text-slate-400">Drag a word to a gap, or click the word and then the gap. Click a filled gap to clear it.</p>
    </div>
  );
}

export function Reorder({ set, responses, setResp }) {
  const q = set.questions[0];
  const paragraphs = asArray(q?.paragraphs);
  const order = responses[q?.qid]?.order || paragraphs.map((_, i) => i);
  const move = (pos, dir) => {
    const next = [...order];
    const to = pos + dir;
    if (to < 0 || to >= next.length) return;
    [next[pos], next[to]] = [next[to], next[pos]];
    setResp(q.qid, { order: next });
  };
  const [dragPos, setDragPos] = useState(null);
  return (
    <div className="space-y-2" data-testid="reorder-list">
      {order.map((pIdx, pos) => (
        <div
          key={pIdx}
          draggable
          onDragStart={() => setDragPos(pos)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={() => {
            if (dragPos == null || dragPos === pos) return;
            const next = [...order];
            const [moved] = next.splice(dragPos, 1);
            next.splice(pos, 0, moved);
            setResp(q.qid, { order: next });
            setDragPos(null);
          }}
          className="flex cursor-grab items-stretch border border-slate-300 bg-white active:cursor-grabbing"
        >
          <div className="flex w-10 shrink-0 items-center justify-center border-r border-slate-200 bg-slate-50 text-sm font-semibold text-slate-500">{pos + 1}</div>
          <p className="flex-1 px-4 py-3 text-[15px] leading-7 text-slate-800">{paragraphs[pIdx]}</p>
          <div className="flex shrink-0 flex-col border-l border-slate-200">
            <button type="button" className="flex-1 px-2 text-slate-500 hover:bg-slate-100 disabled:opacity-30" disabled={pos === 0} onClick={() => move(pos, -1)} aria-label="Move up"><ArrowUp className="h-4 w-4" /></button>
            <button type="button" className="flex-1 border-t border-slate-200 px-2 text-slate-500 hover:bg-slate-100 disabled:opacity-30" disabled={pos === order.length - 1} onClick={() => move(pos, 1)} aria-label="Move down"><ArrowDown className="h-4 w-4" /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

export function McqOptions({ q, multi, responses, setResp }) {
  const chosen = multi ? (responses[q.qid]?.answer_indices || []) : responses[q.qid]?.answer_index;
  return (
    <div className="space-y-2" data-testid={`mcq-${q.qid}`}>
      {asArray(q.options).map((opt, oi) => {
        const active = multi ? chosen.includes(oi) : chosen === oi;
        return (
          <button
            key={oi}
            type="button"
            onClick={() => {
              if (multi) {
                const next = active ? chosen.filter((c) => c !== oi) : [...chosen, oi];
                setResp(q.qid, { answer_indices: next });
              } else {
                setResp(q.qid, { answer_index: oi });
              }
            }}
            className={cn(
              'flex w-full items-start gap-3 border px-4 py-3 text-left text-[15px] leading-6',
              active ? 'border-yellow-500 bg-yellow-100 text-slate-900' : 'border-slate-300 bg-white text-slate-700 hover:border-slate-500'
            )}
          >
            <span className={cn('mt-1 h-3.5 w-3.5 shrink-0 border', multi ? 'rounded-none' : 'rounded-full', active ? 'border-yellow-600 bg-yellow-500' : 'border-slate-400 bg-white')} />
            {opt}
          </button>
        );
      })}
    </div>
  );
}

export function FibTyped({ set, responses, setResp }) {
  const byGap = useMemo(() => Object.fromEntries(set.questions.map((q) => [q.gap, q])), [set]);
  return (
    <GapText
      text={set.stimulus?.display_text}
      renderGap={(gap) => {
        const q = byGap[gap];
        if (!q) return null;
        return (
          <input
            type="text"
            className="mx-1 inline-block w-32 border-b-2 border-slate-400 bg-slate-50 px-2 py-0.5 text-center text-[15px] font-medium text-slate-900 focus:border-blue-900 focus:outline-none"
            value={responses[q.qid]?.answer || ''}
            data-testid={`fib-typed-${q.qid}`}
            onChange={(e) => setResp(q.qid, { answer: e.target.value })}
            autoComplete="off"
            spellCheck="false"
          />
        );
      }}
    />
  );
}

export function HiwWords({ set, responses, setResp }) {
  const q = set.questions[0];
  const selected = responses[q.qid]?.selected || [];
  const toggle = (i) => {
    const next = selected.includes(i) ? selected.filter((s) => s !== i) : [...selected, i];
    setResp(q.qid, { selected: next });
  };
  return (
    <p className="text-[16px] leading-9 text-slate-800" data-testid="hiw-words">
      {asArray(q.display_words).map((w, i) => (
        <React.Fragment key={i}>
          <span
            role="button"
            tabIndex={0}
            onClick={() => toggle(i)}
            onKeyDown={(e) => e.key === 'Enter' && toggle(i)}
            className={cn('cursor-pointer rounded-sm px-0.5', selected.includes(i) ? 'bg-yellow-300 font-medium' : 'hover:bg-slate-200')}
          >
            {w}
          </span>{' '}
        </React.Fragment>
      ))}
    </p>
  );
}

// ---- review renderer --------------------------------------------------------

export function ReviewBlock({ set, results }) {
  const byQid = Object.fromEntries(results.map((r) => [r.qid, r]));
  return (
    <div className="space-y-4">
      {set.questions.map((q) => {
        const r = byQid[q.qid];
        if (!r) return null;
        return (
          <div key={q.qid} className={cn('border-l-2 bg-white px-5 py-4', r.correct ? 'border-emerald-500' : 'border-red-400')}>
            <p className="mono-stat">{r.earned}/{r.max} {r.correct ? 'correct' : 'points'}</p>
            {r.interaction === 'reorder' && (
              <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm leading-6 text-slate-700">
                {(r.correct_order || []).map((pi) => <li key={pi}>{q.paragraphs?.[pi]}</li>)}
              </ol>
            )}
            {(r.interaction === 'fib_drag' || r.interaction === 'fib_typed') && (
              <p className="mt-1 text-sm text-slate-700">
                Gap answer: <span className="font-semibold">{r.answer}</span>
                {!r.correct && r.your_answer ? <> · you wrote <span className="line-through">{r.your_answer}</span></> : null}
              </p>
            )}
            {r.interaction === 'wfd' && (
              <div className="mt-1 space-y-1 text-sm text-slate-700">
                <p>Sentence: <span className="font-semibold">{r.answer_text}</span></p>
                <p>You typed: {r.your_text || <span className="text-slate-400">nothing</span>}</p>
              </div>
            )}
            {r.interaction === 'hiw' && (
              <div className="mt-1 space-y-1 text-sm text-slate-700">
                <p>Differing words at positions {(r.incorrect_indices || []).map((i) => `"${q.display_words?.[i]}"`).join(', ')}.</p>
                {r.corrections && (
                  <p>The speaker actually said: {Object.entries(r.corrections).map(([i, w]) => `${q.display_words?.[Number(i)]} → ${w}`).join('; ')}.</p>
                )}
                {r.wrong_clicks?.length > 0 && <p className="text-red-700">Wrong clicks cost points: {r.wrong_clicks.map((i) => `"${q.display_words?.[i]}"`).join(', ')}.</p>}
              </div>
            )}
            {(r.interaction === 'mcq_single' || r.interaction === 'fib_dropdown') && q.options && (
              <p className="mt-1 text-sm text-slate-700">
                Answer: <span className="font-semibold">{q.options[r.answer_index]}</span>
                {!r.correct && r.your_answer != null ? <> · you chose <span className="line-through">{q.options[r.your_answer]}</span></> : null}
              </p>
            )}
            {r.interaction === 'mcq_multi' && q.options && (
              <div className="mt-1 space-y-1 text-sm text-slate-700">
                <p>Correct: {(r.answer_indices || []).map((i) => `"${q.options[i]}"`).join(', ')}</p>
                {r.wrong_selections?.length > 0 && <p className="text-red-700">Selections that LOST points: {r.wrong_selections.map((i) => `"${q.options[i]}"`).join(', ')}</p>}
              </div>
            )}
            {r.trap_label && (
              <span className="mt-2 inline-flex items-center gap-1.5 rounded bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700">
                Trap: {r.trap_label}
              </span>
            )}
            {r.explanation && <p className="mt-2 text-sm leading-6 text-slate-600">{r.explanation}</p>}
            {r.why_yours_attracts && (
              <p className="mt-2 border-l-2 border-amber-400 bg-amber-50/50 px-3 py-2 text-sm leading-6 text-slate-700">
                <span className="font-semibold">Why your pick attracts:</span> {r.why_yours_attracts}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}


// Builds the responses array for a set from the local response map
export function buildResponses(set, responses) {
  return set.questions.map((q) => ({ qid: q.qid, ...(responses[q.qid] || {}) }));
}
